import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";

const MAX_ATTEMPTS = 6;

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | failed | timeout | error
  const [info, setInfo] = useState(null);

  const poll = useCallback(
    async (attempt) => {
      if (attempt >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setState("error");
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  return (
    <main className="min-h-screen bg-sand flex items-center justify-center px-6" data-testid="payment-status-page">
      <div className="max-w-md w-full text-center bg-surface rounded-[2rem] p-12">
        {state === "polling" && (
          <>
            <Loader2 className="mx-auto text-forest animate-spin" size={40} />
            <h1 className="font-serif text-3xl text-ink mt-6">Confirming your booking…</h1>
            <p className="text-muted mt-3">Please hold on a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-forest" size={48} />
            <h1 className="font-serif text-3xl text-ink mt-6" data-testid="payment-success">
              You're booked.
            </h1>
            <p className="text-muted mt-3">
              Thank you for reserving the <span className="text-forest">{info?.package_name}</span>. A
              confirmation will arrive in your inbox shortly.
            </p>
          </>
        )}
        {(state === "failed" || state === "timeout" || state === "error") && (
          <>
            <XCircle className="mx-auto text-clay" size={48} />
            <h1 className="font-serif text-3xl text-ink mt-6" data-testid="payment-failed">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="text-muted mt-3">
              {state === "timeout"
                ? "Your payment is taking longer than expected. Please check your email for confirmation."
                : "We couldn't confirm your payment. No charge was made — please try again."}
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="back-home-btn"
          className="mt-10 inline-flex items-center gap-2 rounded-full bg-forest text-sand px-7 py-3.5 text-sm hover:bg-forest-deep transition-colors"
        >
          <ArrowLeft size={16} /> Back to home
        </Link>
      </div>
    </main>
  );
}
